import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal, Trash2, Zap, PenLine, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";

const PAGE_OPTIONS = ["Pricing", "Features", "FAQ", "Landing"];

const EMPTY_FORM = {
  name: "",
  min_intent: 85,
  min_dwell: 45,
  revisit_min: 0,
  pages: ["Pricing"],
  action: "auto_draft",
  enabled: true,
};

function RuleCard({ rule, onToggle, onDelete }) {
  const isSend = rule.action === "auto_send";
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid={`rule-card-${rule.rule_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{rule.name}</span>
            <Badge variant="outline" className={`text-[10px] font-mono ${isSend ? "bg-rose-500/15 text-rose-300 border-rose-400/20" : "bg-sky-500/15 text-sky-300 border-sky-400/20"}`}>
              {isSend ? <><Zap className="w-2.5 h-2.5 mr-1" /> Auto-send</> : <><PenLine className="w-2.5 h-2.5 mr-1" /> Auto-draft</>}
            </Badge>
          </div>
          <div className="mt-1.5 text-xs text-slate-400 font-mono">
            intent ≥ {rule.min_intent} · dwell ≥ {rule.min_dwell}s
            {rule.revisit_min > 0 && <> · revisits ≥ {rule.revisit_min}</>}
            {rule.pages?.length > 0 && <> · {rule.pages.join(", ")}</>}
          </div>
          <div className="mt-1 text-[10px] font-mono text-slate-500">
            Fired {rule.fired_count || 0}× {rule.last_fired_at && `· last ${new Date(rule.last_fired_at).toLocaleTimeString()}`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={rule.enabled}
            onCheckedChange={(v) => onToggle(rule.rule_id, v)}
            data-testid={`rule-toggle-${rule.rule_id}`}
          />
          <button onClick={() => onDelete(rule.rule_id)} data-testid={`rule-delete-${rule.rule_id}`} className="text-slate-500 hover:text-rose-300 p-1 rounded-md hover:bg-white/[0.04]">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SignalRulesPanel({ onRulesChanged }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/signal-rules");
      setRules(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePage = (p) => {
    setForm((f) => ({
      ...f,
      pages: f.pages.includes(p) ? f.pages.filter((x) => x !== p) : [...f.pages, p],
    }));
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Give the rule a name"); return; }
    setCreating(true);
    try {
      await api.post("/signal-rules", form);
      toast.success("Rule created");
      setForm(EMPTY_FORM);
      await load();
      onRulesChanged?.();
    } catch {
      toast.error("Could not create rule");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (id, enabled) => {
    setRules((prev) => prev.map((r) => (r.rule_id === id ? { ...r, enabled } : r)));
    try {
      await api.patch(`/signal-rules/${id}`, { enabled });
      onRulesChanged?.();
    } catch {
      toast.error("Toggle failed");
      load();
    }
  };

  const remove = async (id) => {
    setRules((prev) => prev.filter((r) => r.rule_id !== id));
    await api.delete(`/signal-rules/${id}`);
    onRulesChanged?.();
  };

  const activeCount = rules.filter((r) => r.enabled).length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm" variant="outline" data-testid="signals-rules-btn" className="h-9 border-white/10 bg-white/[0.03]">
          <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
          Rules
          {activeCount > 0 && <span className="ml-1.5 text-[10px] font-mono text-emerald-300">· {activeCount} on</span>}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#0B1020] border-l border-white/10 text-slate-100 overflow-y-auto" data-testid="signals-rules-panel">
        <SheetHeader>
          <SheetTitle className="text-slate-100">Signal rules</SheetTitle>
          <SheetDescription className="text-slate-400 text-sm">
            When a new visit matches all conditions, the AI drafts (and optionally sends) the follow-up automatically.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>}
          {!loading && rules.length === 0 && <div className="text-sm text-slate-500">No rules yet.</div>}
          {rules.map((r) => (
            <RuleCard key={r.rule_id} rule={r} onToggle={toggle} onDelete={remove} />
          ))}
        </div>

        <div className="mt-8 rounded-xl border border-dashed border-white/10 p-4" data-testid="rule-create-form">
          <div className="text-[10px] uppercase font-mono tracking-widest text-emerald-300 flex items-center gap-1 mb-3">
            <Plus className="w-3 h-3" /> New rule
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="rule-name" className="text-xs text-slate-400">Name</Label>
              <Input
                id="rule-name"
                data-testid="rule-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Enterprise pricing revisits"
                className="mt-1 bg-white/[0.03] border-white/10 h-9 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Min intent</Label>
                <Input
                  type="number" min={0} max={100}
                  data-testid="rule-intent-input"
                  value={form.min_intent}
                  onChange={(e) => setForm({ ...form, min_intent: parseInt(e.target.value || "0", 10) })}
                  className="mt-1 bg-white/[0.03] border-white/10 h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Min dwell (s)</Label>
                <Input
                  type="number" min={0}
                  data-testid="rule-dwell-input"
                  value={form.min_dwell}
                  onChange={(e) => setForm({ ...form, min_dwell: parseInt(e.target.value || "0", 10) })}
                  className="mt-1 bg-white/[0.03] border-white/10 h-9 text-sm"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-400">Pages (any of)</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {PAGE_OPTIONS.map((p) => {
                  const on = form.pages.includes(p);
                  return (
                    <button
                      key={p}
                      onClick={() => togglePage(p)}
                      data-testid={`rule-page-${p.toLowerCase()}`}
                      className={`h-7 px-2.5 rounded-full text-xs font-mono border transition ${on ? "bg-sky-500/20 border-sky-400/30 text-sky-200" : "bg-white/[0.02] border-white/10 text-slate-400 hover:text-white"}`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-400">Action</Label>
              <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
                <SelectTrigger data-testid="rule-action-trigger" className="mt-1 bg-white/[0.03] border-white/10 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1020] border-white/10 text-slate-100">
                  <SelectItem value="auto_draft" data-testid="rule-action-draft">Auto-draft (queue for review)</SelectItem>
                  <SelectItem value="auto_send" data-testid="rule-action-send">Auto-send immediately</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={create}
              disabled={creating}
              data-testid="rule-create-btn"
              className="w-full h-9 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            >
              {creating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Creating…</> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save rule</>}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
